import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCheck,
  faChevronRight,
  faClock,
  faCog,
  faCopy,
  faDownload,
  faFilter,
  faHistory,
  faInbox,
  faPlus,
  faSync,
  faTerminal,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import SEO, { ROBOTS_NOINDEX } from '../components/SEO';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import Panel from '../components/layout/Panel';
import CopyButton from '../components/CopyButton';
import RequestDetail from '../components/RequestDetail';
import { DESKTOP_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import type { EndpointData, Request, RequestPreview } from '../types';
import { formatBytes, formatDate, methodVariant, retentionLabel, retentionVariant, statusVariant } from '../types';
import { generateStarterCurlCommand } from '../utils/curl';
import { REQUEST_API_BASE, buildEndpointUrl, getPublicApiBase, slugFromEndpointUrl } from '../utils/publicUrl';
import { ResponseSettingError, isBodylessStatus, responseHeaderRowError, responseHeaderRows, responseHeaderRowsToBag, responseHeadersGroupError, responseSettingError } from '../utils/responseSettings';
import type { ResponseHeaderRow } from '../utils/responseSettings';
import { useEndpoints } from '../hooks/useEndpoints';
import './EndpointView.css';

/**
 * Request base for axios — relative `/api` in production, which is what keeps
 * the calls same-origin. Never render this to a user: it is not a URL they can
 * paste. Displayed/copied/exported URLs come from `buildEndpointUrl` below.
 */
const API_URL = REQUEST_API_BASE;

/** Absolute base for every endpoint URL this page shows. */
const PUBLIC_API_BASE = getPublicApiBase();

/** How often the inspector re-polls while live mode is on. */
const POLL_INTERVAL_MS = 5000;

/** Highest id in a list — the cursor the next live poll asks from. */
const highestRequestId = (requests: RequestPreview[]): number =>
  requests.reduce((highest, request) => (request.id > highest ? request.id : highest), 0);

/**
 * How long the search box waits after the last keystroke before it asks the
 * server. The filter runs in the database over every stored row, not over the
 * page this browser downloaded, so a request per keystroke would be a scan per
 * keystroke — and a slow, flapping list on a phone keyboard.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The filter parameters one request carries.
 *
 * Both are omitted unless set: an absent parameter is how the server is told to
 * apply no filter at all, which is not the same as filtering on an empty string
 * or on the literal method `all`.
 */
const filterParams = (term: string, method: string): Record<string, string> => ({
  ...(term === '' ? {} : { q: term }),
  ...(method === 'all' ? {} : { method }),
});

const EndpointView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [data, setData] = useState<EndpointData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<RequestPreview | null>(null);
  /**
   * Full records for rows that have been opened, keyed by id.
   *
   * The list only carries previews, so the detail pane fetches `GET
   * /request/:id` the first time a row is selected and keeps the answer here:
   * re-selecting a row the user already looked at must not re-download it.
   */
  const [details, setDetails] = useState<Record<number, Request>>({});
  /** Ids whose detail fetch is in flight, so a re-render cannot start a second one. */
  const pendingDetailsRef = useRef<Set<number>>(new Set());
  const detailsRef = useRef<Record<number, Request>>({});
  /** Why the detail of `id` could not be shown — only for the selected row. */
  const [detailError, setDetailError] = useState<{ id: number; message: string } | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  /**
   * What the user typed, not a number: clearing the field has to stay visible,
   * and a rejected save has to be correctable. The API is the one authority on
   * what a valid code is, and its 400 message is what the field renders.
   */
  const [responseStatusCode, setResponseStatusCode] = useState('200');
  /**
   * The stored body, or `undefined` when the endpoint has none configured
   * (`null`). The two "no text" states stay distinct: an untouched, never
   * configured body is left as-is on save, while a body the user clears in the
   * field is saved as `''` so the endpoint answers zero bytes.
   */
  const [responseBody, setResponseBody] = useState<string | undefined>(undefined);
  const [responseContentType, setResponseContentType] = useState<string>('application/json');
  /**
   * Extra headers the endpoint answers with, as rows of what the user typed.
   *
   * Rows rather than a map: the form has to show a name the API rejected, and
   * two rows mid-edit may share a name without collapsing into one. The map is
   * built once, on save.
   */
  const [headerRows, setHeaderRows] = useState<ResponseHeaderRow[]>([]);
  /** The API's rejection of the last save, shown next to the field it names. */
  const [settingsError, setSettingsError] = useState<ResponseSettingError | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [methodFilter, setMethodFilter] = useState('all');
  /** What the user typed. The list itself is filtered by the server. */
  const [query, setQuery] = useState('');
  /**
   * The term the server was last asked for — `query` once the debounce has
   * settled. The request, the counters and the "nothing matches" copy all
   * describe this term rather than the one still being typed, so the page never
   * claims a filter the rows on screen were not fetched with.
   */
  const [appliedQuery, setAppliedQuery] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEndpoint, setDeletingEndpoint] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  /**
   * The list the last poll asked from. Reading `data` directly inside
   * `fetchEndpointData` would give that callback a new identity on every poll,
   * which tears down and restarts the interval — and it would be stale inside
   * the timer. A ref keeps the poll's cursor in sync with whatever the list
   * currently holds, including after a delete or a clear.
   */
  const heldDataRef = useRef<EndpointData | null>(null);

  useEffect(() => {
    heldDataRef.current = data;
  }, [data]);

  /**
   * Debounce the search box: the term is the server's query, so it is sent once
   * the typing stops rather than once per keystroke. Trimming here means a box
   * holding only spaces asks for no filter, which is exactly how the server
   * reads it.
   */
  useEffect(() => {
    const timer = setTimeout(() => setAppliedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * A filter change is a different list: it starts at its first row again, and
   * a modal open on a narrow screen is closed, because it describes a row the
   * new answer need not carry. The ref keeps the first render — nothing typed
   * yet, no filter — from counting as a change.
   */
  const appliedFiltersRef = useRef({ query: appliedQuery, method: methodFilter });
  useEffect(() => {
    const previous = appliedFiltersRef.current;
    appliedFiltersRef.current = { query: appliedQuery, method: methodFilter };
    if (previous.query === appliedQuery && previous.method === methodFilter) {
      return;
    }
    setActiveIndex(0);
    setSelectedRequest(null);
  }, [appliedQuery, methodFilter]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  /**
   * `quiet` is used by the live poll: it must not flash the spinner, clear the
   * error banner or reset the selected request every five seconds. A quiet poll
   * also asks only for what arrived after the newest row already on screen
   * (`since`), so the 5 s timer stops re-downloading bodies the user is looking
   * at. The first load and the Refresh button stay full fetches.
   *
   * The active filters travel with every one of those requests — the first
   * load, a refresh and the poll alike — because they are the server's filters,
   * not this component's: an unfiltered `since` poll would inject rows the user
   * filtered out, and an unfiltered refresh would silently drop the filter.
   */
  const fetchEndpointData = useCallback(async (quiet = false) => {
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    // A quiet poll asks only for what arrived after the newest row already on
    // screen, so the 5 s timer never re-downloads bodies the user is looking at.
    const held = quiet ? heldDataRef.current : null;
    const since = held ? highestRequestId(held.requests) : null;
    const params = { url: slug, ...filterParams(appliedQuery, methodFilter) };
    try {
      const response = await axios.get(`${API_URL}/view`, {
        params: since === null ? params : { ...params, since },
      });
      // The API answers an unknown slug with HTTP 200 and an error envelope
      // (`{ error: 'Endpoint not found', url }`) instead of a 404, so axios
      // never throws for it. Only a payload carrying `endpoint` is renderable.
      const payload = response.data;
      if (!payload || !payload.endpoint) {
        setData(null);
        setError(payload?.error || 'Endpoint not found');
        return;
      }
      if (since === null) {
        setData(payload);
        if (!quiet) {
          setActiveIndex(0);
        }
        return;
      }
      // Incremental answer: the rows already held stay exactly where they are
      // (same keys, so no selection, filter or scroll state is disturbed) and
      // only genuinely new ids are prepended. Filtering against `prev` keeps an
      // overlapping poll from appending the same row twice.
      setData((prev) => {
        const current = prev ?? { endpoint: payload.endpoint, requests: [], requestCount: 0 };
        const known = new Set(current.requests.map((request) => request.id));
        const fresh = (payload.requests ?? []).filter(
          (request: Request) => !known.has(request.id),
        );
        const merged = [...fresh, ...current.requests];
        // `requestCount` is the server's total, not this slice. Once an endpoint
        // is at MAX_REQUESTS_PER_ENDPOINT the server drops its oldest rows, so
        // an append-only merge would keep listing (and counting) rows the
        // endpoint no longer holds and contradict the header. The list is
        // newest-first, so the newest `requestCount` rows are exactly the ones
        // still stored; anything past that is stale and goes. A payload without
        // a usable count leaves the merge alone rather than truncating it.
        const serverCount = Number.isFinite(payload.requestCount)
          ? Math.max(0, payload.requestCount)
          : merged.length;
        return { ...payload, requests: merged.slice(0, serverCount) };
      });
    } catch (err: any) {
      if (!quiet) {
        setError(err.response?.data?.error || 'Failed to load endpoint data');
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
    // The filters are dependencies, not refs: a new term or method has to give
    // this callback a new identity so the fetch effect below runs it again.
  }, [slug, appliedQuery, methodFilter]);

  const fetchEndpointSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/manage/endpoint/${slug}/settings`);
      // The stored code is rendered as the text the field holds, so a legacy
      // out-of-range row is at least visible and correctable.
      setResponseStatusCode(String(response.data.responseStatusCode ?? 200));
      // `null` (never configured) stays unsupplied; `''` (an explicitly empty
      // body) is loaded and saved back as the empty string it is.
      setResponseBody(response.data.responseBody ?? undefined);
      setResponseContentType(response.data.responseContentType || 'application/json');
      // The stored bag, as rows. `null` (never configured) is no rows.
      setHeaderRows(responseHeaderRows(response.data.responseHeaders));
    } catch (err) {
      console.error('Failed to load endpoint settings:', err);
    }
  }, [slug]);

  // The list and the settings live in separate effects: a keystroke re-runs the
  // list query, and it must not re-read the settings with it.
  useEffect(() => {
    fetchEndpointData();
  }, [fetchEndpointData]);

  useEffect(() => {
    fetchEndpointSettings();
  }, [fetchEndpointSettings]);

  /**
   * Fetch the full record behind a row, once.
   *
   * The list answer deliberately carries previews only, so headers, the whole
   * body and `bodyRaw` come from the detail endpoint when a row is opened. The
   * cache and the in-flight set are refs so that re-selecting a row — or a
   * re-render while its first fetch is still open — cannot produce a second
   * request for it.
   */
  const loadRequestDetail = useCallback(async (id: number) => {
    if (detailsRef.current[id] || pendingDetailsRef.current.has(id)) {
      return;
    }
    pendingDetailsRef.current.add(id);
    try {
      // The detail route is scoped by slug, exactly like the delete route: a
      // bare id would let anyone enumerate every capture on the service. The
      // slug comes from the route this page is already viewing.
      const response = await axios.get(
        `${API_URL}/request/${id}?url=${encodeURIComponent(slug ?? '')}`,
      );
      const payload = response.data;
      // Like `/view`, the API answers a miss with HTTP 200 and an error
      // envelope, so a payload without an id is not renderable.
      if (!payload || typeof payload.id !== 'number') {
        setDetailError({ id, message: payload?.error || 'Failed to load this request' });
        return;
      }
      detailsRef.current = { ...detailsRef.current, [id]: payload as Request };
      setDetails(detailsRef.current);
    } catch (err: any) {
      setDetailError({
        id,
        message: err.response?.data?.error || 'Failed to load this request',
      });
    } finally {
      pendingDetailsRef.current.delete(id);
    }
  }, [slug]);

  // Live mode: poll quietly so a webhook you are about to fire shows up without
  // anyone reaching for the Refresh button.
  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const timer = setInterval(() => fetchEndpointData(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchEndpointData]);

  /**
   * Open the settings modal with no stale rejection left over from the last
   * attempt: the error belongs to the values the user is about to edit.
   */
  const openSettings = () => {
    setSettingsError(null);
    setShowSettings(true);
  };

  /** Add a header row, edit one in place, or drop one. */
  const addHeaderRow = () => {
    setHeaderRows((rows) => [...rows, { name: '', value: '' }]);
  };

  const updateHeaderRow = (index: number, patch: Partial<ResponseHeaderRow>) => {
    setHeaderRows((rows) =>
      rows.map((row, position) => (position === index ? { ...row, ...patch } : row)),
    );
  };

  const removeHeaderRow = (index: number) => {
    setHeaderRows((rows) => rows.filter((_, position) => position !== index));
  };

  /** The rejection that names no single row: too many headers, or a bag over the limit. */
  const headersGroupError = responseHeadersGroupError(settingsError, headerRows);

  /**
   * Persist the response settings.
   *
   * The values go to the API exactly as typed — a cleared status-code field
   * included — because the API is what decides whether a setting is storable.
   * A rejection keeps the modal open and renders the API's own message next to
   * the field it names, so the value can be corrected instead of reported
   * through an `alert()` the user cannot act on.
   */
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API_URL}/manage/endpoint/${slug}/settings`, {
        responseStatusCode,
        responseBody,
        responseContentType,
        // The rows as a bag, exactly as typed: an empty row the user never
        // filled in adds nothing, and an empty list clears the stored headers.
        responseHeaders: responseHeaderRowsToBag(headerRows),
      });
      setSettingsError(null);
      setShowSettings(false);
    } catch (err: any) {
      setSettingsError(responseSettingError(err, 'Failed to save settings'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const copyUrl = async () => {
    await copyToClipboard(buildEndpointUrl(slug ?? ''), 'url');
  };

  /**
   * Export the rows this browser has loaded.
   *
   * Export All is bounded by what is loaded — fetching every stored row to
   * write the file would be precisely the download the paging exists to avoid —
   * and the panel says so next to the button. A row the user has opened
   * carries its full record from the cache; one that has not been opened
   * carries its preview and nulls where the full record would be. The response
   * record follows the same rule: the status is on the preview, its content
   * type and body size only on the full record.
   */
  const exportRequests = () => {
    if (!data?.endpoint || !data.requests?.length) {
      return;
    }

    const exportData = {
      endpoint: {
        slug: data.endpoint.slug,
        created: data.endpoint.created,
        url: buildEndpointUrl(data.endpoint.slug),
      },
      exportedAt: new Date().toISOString(),
      /** Rows in this file: the loaded page(s), not necessarily every stored row. */
      exportedRows: data.requests.length,
      /** Every row the endpoint holds, however many are in this file. */
      requestCount: data.totalRequestCount ?? data.requestCount,
      requests: data.requests.map((req) => {
        const detail = details[req.id];
        return {
          id: req.id,
          method: req.method,
          path: req.path ?? null,
          receivedAt: req.receivedAt,
          ip: req.ip,
          userAgent: req.userAgent,
          bodyBytes: req.bodyBytes,
          bodyPreview: req.bodyPreview ?? null,
          // What the endpoint answered. The status travels with the preview,
          // the rest of the response record only with the full record.
          status: req.status ?? null,
          // Full record when this row has been opened, null when only its
          // preview was ever downloaded.
          headers: detail?.headers ?? null,
          queryParams: detail?.queryParams ?? null,
          body: detail?.body ?? null,
          bodyRaw: detail?.bodyRaw ?? null,
          responseContentType: detail?.responseContentType ?? null,
          responseHeaders: detail?.responseHeaders ?? null,
          responseBodyBytes: detail?.responseBodyBytes ?? null,
        };
      }),
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `requests-${slug}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportSingleRequest = (request: Request) => {
    const exportData = {
      endpoint: {
        slug: data?.endpoint?.slug,
        created: data?.endpoint?.created,
        url: buildEndpointUrl(data?.endpoint?.slug ?? slug ?? ''),
      },
      exportedAt: new Date().toISOString(),
      request: {
        id: request.id,
        method: request.method,
        path: request.path ?? null,
        receivedAt: request.receivedAt,
        headers: request.headers,
        queryParams: request.queryParams,
        body: request.body,
        bodyRaw: request.bodyRaw ?? null,
        ip: request.ip,
        userAgent: request.userAgent,
        // What the endpoint answered, as recorded at capture time.
        status: request.status ?? null,
        responseContentType: request.responseContentType ?? null,
        responseHeaders: request.responseHeaders ?? null,
        responseBodyBytes: request.responseBodyBytes ?? null,
      },
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `request-${request.id}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const allRequests = useMemo(() => data?.requests ?? [], [data]);

  /**
   * The name the user actually chose for this endpoint on Home (its
   * browser-local label), or '' when the header should just fall back to the
   * slug.
   *
   * The saved list is per-browser, so a slug opened on another machine has no
   * label here at all. A freshly generated one does: `saveEndpoint` seeds the
   * label with the slug itself, so "never renamed" arrives as `label === slug`
   * rather than as a missing label — and rendering that name would print the
   * same characters twice. Read-only: renaming stays on Home.
   */
  const { endpoints: savedEndpoints, removeEndpoint } = useEndpoints();
  const savedLabel = useMemo(() => {
    const current = data?.endpoint?.slug;
    if (!current) {
      return '';
    }
    const match = savedEndpoints.find((ep) => slugFromEndpointUrl(ep.url) === current);
    const label = (match?.label || '').trim();
    // Only a name the user actually typed is worth showing.
    return label === current ? '' : label;
  }, [savedEndpoints, data]);

  /**
   * The methods the endpoint holds, as the API answers them: unfiltered, so the
   * option currently being filtered by is always still in the picker — and so
   * it offers a verb that only appears on a page the browser has not loaded.
   */
  const methods = useMemo(() => Array.from(new Set(data?.methods ?? [])).sort(), [data]);

  /**
   * The free-text box is not a filter here; it is one half of the server query.
   * `allRequests` already holds exactly the rows that answer it.
   */
  const filterApplied = appliedQuery !== '' || methodFilter !== 'all';

  /**
   * Every row the endpoint holds, whatever the filter is. The header, the
   * destructive confirmations and Export All's own `requestCount` describe the
   * endpoint, so a filter must not shrink them to the filtered subset.
   */
  const endpointRequestCount = data?.totalRequestCount ?? data?.requestCount ?? 0;

  const safeIndex = allRequests.length === 0 ? 0 : Math.min(activeIndex, allRequests.length - 1);
  /** Wide screens keep the selected request in the detail pane; narrow ones open the modal. */
  const activePreview = isDesktop ? allRequests[safeIndex] ?? null : selectedRequest;
  /**
   * The full record of whichever row is selected. The list only carries
   * previews, so `RequestDetail` gets the fetched record — and until that
   * lands (or if it fails), a placeholder instead of an empty inspector.
   */
  const activeRequest = activePreview ? details[activePreview.id] ?? null : null;
  const activeDetailError =
    activePreview && detailError?.id === activePreview.id ? detailError.message : null;

  const activePreviewId = activePreview ? activePreview.id : null;
  useEffect(() => {
    if (activePreviewId !== null) {
      loadRequestDetail(activePreviewId);
    }
  }, [activePreviewId, loadRequestDetail]);

  /** Is there an older page the server has not sent yet? */
  const hasOlderRequests = Boolean(data) && allRequests.length < (data?.requestCount ?? 0);

  /**
   * Append the page just older than the oldest loaded row.
   *
   * `before` is exclusive on the server, so the next page starts below the
   * oldest id held here; the ids are filtered again anyway, because a page that
   * overlapped (a delete racing the request) must not produce duplicate rows.
   */
  const loadOlderRequests = async () => {
    const oldest = allRequests[allRequests.length - 1];
    if (!oldest || loadingOlder) {
      return;
    }
    setLoadingOlder(true);
    setActionError(null);
    try {
      const response = await axios.get(`${API_URL}/view`, {
        params: { url: slug, before: oldest.id, ...filterParams(appliedQuery, methodFilter) },
      });
      const payload = response.data;
      if (!payload || !payload.endpoint) {
        setActionError(payload?.error || 'Failed to load older requests');
        return;
      }
      setData((prev) => {
        const current = prev ?? {
          endpoint: payload.endpoint,
          requests: [],
          requestCount: 0,
          totalRequestCount: 0,
          methods: [],
        };
        const known = new Set(current.requests.map((request) => request.id));
        const older = (payload.requests ?? []).filter(
          (request: RequestPreview) => !known.has(request.id),
        );
        return {
          ...current,
          requests: [...current.requests, ...older],
          requestCount: Number.isFinite(payload.requestCount)
            ? Math.max(0, payload.requestCount)
            : current.requestCount,
        };
      });
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to load older requests');
    } finally {
      setLoadingOlder(false);
    }
  };

  const selectRequest = (index: number) => {
    if (isDesktop) {
      setActiveIndex(index);
      return;
    }
    setSelectedRequest(allRequests[index]);
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = Math.min(Math.max(index + delta, 0), allRequests.length - 1);
    selectRequest(nextIndex);
    rowRefs.current[nextIndex]?.focus();
  };

  /**
   * Delete one captured request. The API call is scoped by slug, so an id alone
   * can never reach another endpoint's captures.
   */
  const deleteRequest = async (request: RequestPreview) => {
    setDeletingId(request.id);
    setActionError(null);
    try {
      await axios.delete(`${API_URL}/request/${request.id}`, {
        params: { url: slug },
      });

      // Read the position before the list shrinks: row `index + 1` is what
      // slides into the deleted row's place.
      const index = allRequests.findIndex((entry) => entry.id === request.id);
      const wasSelectedOnNarrow = selectedRequest?.id === request.id;

      setData((prev) => {
        if (!prev) {
          return prev;
        }
        const remaining = prev.requests.filter((entry) => entry.id !== request.id);
        return {
          ...prev,
          requests: remaining,
          // Both counts shrink by the one row: what is left to load, and what
          // the endpoint holds.
          requestCount: Math.max(0, prev.requestCount - (prev.requests.length - remaining.length)),
          totalRequestCount: Math.max(
            0,
            (prev.totalRequestCount ?? prev.requestCount) -
              (prev.requests.length - remaining.length),
          ),
        };
      });

      if (index !== -1) {
        // Removing a row above the selection keeps the same request selected;
        // removing the selected row leaves the index on the next remaining one.
        setActiveIndex((current) => (index < current ? current - 1 : current));
        if (wasSelectedOnNarrow) {
          setSelectedRequest(allRequests[index + 1] ?? allRequests[index - 1] ?? null);
        }
      }

      // The row is gone, so its cached record is dead weight — and would be
      // served from the cache if an id ever came back.
      if (detailsRef.current[request.id]) {
        const remainingDetails = { ...detailsRef.current };
        delete remainingDetails[request.id];
        detailsRef.current = remainingDetails;
        setDetails(remainingDetails);
      }
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to delete the request');
    } finally {
      setDeletingId(null);
    }
  };

  /** Delete every captured request, keeping the endpoint and its settings. */
  const clearAllRequests = async () => {
    setClearing(true);
    setActionError(null);
    try {
      await axios.delete(`${API_URL}/manage/endpoint/${slug}/requests`);
      setData((prev) =>
        prev
          ? { ...prev, requests: [], requestCount: 0, totalRequestCount: 0, methods: [] }
          : prev,
      );
      setActiveIndex(0);
      setSelectedRequest(null);
      // Nothing is left to inspect, so nothing is left to cache.
      detailsRef.current = {};
      setDetails({});
      setShowClearConfirm(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to clear the captured requests');
      setShowClearConfirm(false);
    } finally {
      setClearing(false);
    }
  };

  /**
   * Delete the endpoint itself, so the URL stops existing.
   *
   * This is the destructive action Clear All is not: it removes the endpoint
   * row (and, by cascade, every captured request), then drops the browser-local
   * saved row for this slug and returns to Home. The confirmation copy says
   * both, because a URL that silently keeps capturing is the bug this fixes.
   */
  const deleteEndpoint = async () => {
    setDeletingEndpoint(true);
    setActionError(null);
    try {
      await axios.delete(`${API_URL}/manage/endpoint/${slug}`);

      // The saved list is browser-local and keyed by slug, so the row for this
      // endpoint (if this browser ever saved it) goes with the endpoint.
      const saved = savedEndpoints.find((ep) => slugFromEndpointUrl(ep.url) === slug);
      if (saved) {
        removeEndpoint(saved.id);
      }

      setShowDeleteConfirm(false);
      navigate('/');
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to delete the endpoint');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingEndpoint(false);
    }
  };

  /* The same control heads both the empty and the populated panel. */
  const liveToggle = (
    <Form.Check
      type="switch"
      id="endpoint-live"
      className="endpoint-live"
      label={
        <span className="endpoint-live__label">
          {autoRefresh && <span className="endpoint-live__dot" aria-hidden="true" />}
          Live
        </span>
      }
      checked={autoRefresh}
      onChange={(event) => setAutoRefresh(event.target.checked)}
      title={`Check for new requests every ${POLL_INTERVAL_MS / 1000} seconds`}
    />
  );

  if (!slug) {
    return (
      <PageShell>
        <Alert variant="warning">Invalid endpoint URL</Alert>
      </PageShell>
    );
  }

  if (loading && !data) {
    return (
      <PageShell>
        <div className="endpoint-loading">
          <Spinner animation="border" role="status" aria-hidden="true" />
          <p>Loading endpoint data...</p>
        </div>
      </PageShell>
    );
  }

  if (error || !data || !data.endpoint) {
    return (
      <PageShell>
        <SEO
          title={`Endpoint ${slug} - What Am I Sending`}
          description={`View captured HTTP requests for endpoint ${slug}.`}
          path={`/endpoint/${slug}`}
          robots={ROBOTS_NOINDEX}
        />
        <Alert variant="danger">
          <Alert.Heading>Unable to load endpoint</Alert.Heading>
          <p>{error || 'Endpoint not found'}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Alert>
      </PageShell>
    );
  }

  /*
   * The starter request offered while the endpoint is empty. Built from the
   * same absolute URL the panel above displays, so the command shown and the
   * command copied are one string, and that string is what a shell runs.
   */
  const starterCurl = generateStarterCurlCommand(buildEndpointUrl(data.endpoint.slug));

  return (
    <PageShell width="wide">
      <SEO
        title={`Endpoint ${data.endpoint.slug} - What Am I Sending`}
        description={`Inspecting ${endpointRequestCount} captured HTTP request(s) for endpoint ${data.endpoint.slug}.`}
        path={`/endpoint/${data.endpoint.slug}`}
        robots={ROBOTS_NOINDEX}
      />

      <PageHeader
        className="endpoint-head"
        icon={faChartLine}
        title={savedLabel ? `Endpoint: ${savedLabel}` : `Endpoint: ${data.endpoint.slug}`}
        subtitle="Inspect every captured request, then replay it as cURL."
        backLink={{ to: '/', label: 'Back to Home' }}
        meta={
          <>
            {/*
              When the header shows the user's name, the slug stays here so the
              copyable identifier never disappears from the page.
            */}
            {savedLabel ? (
              <span className="endpoint-head__slug">
                Slug <code>{data.endpoint.slug}</code>
              </span>
            ) : null}
            <span>Created {formatDate(data.endpoint.created)}</span>
            {/* The URL itself expires too: it goes once nothing has arrived for
                the retention window, so the header says so next to the count. */}
            <span>Deleted after 5 idle days</span>
            <span>
              {endpointRequestCount} request{endpointRequestCount === 1 ? '' : 's'} captured
            </span>
          </>
        }
        actions={
          <>
            <Button variant="outline-secondary" size="sm" onClick={copyUrl} aria-label="Copy endpoint URL">
              <FontAwesomeIcon icon={copiedField === 'url' ? faCheck : faCopy} aria-hidden="true" className="me-2" />
              {copiedField === 'url' ? 'Copied!' : 'Copy URL'}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={openSettings}>
              <FontAwesomeIcon icon={faCog} aria-hidden="true" className="me-2" />
              Settings
            </Button>
          </>
        }
      />

      <Panel variant="quiet" className="endpoint-url-panel">
        <span className="endpoint-url__label">Your endpoint URL</span>
        <code className="endpoint-url">{buildEndpointUrl(data.endpoint.slug)}</code>
      </Panel>

      {actionError ? (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {/*
        An endpoint with nothing captured shows the starter panel; an endpoint
        whose rows are all filtered out shows the list and says so. The two are
        different answers, so they cannot share a branch: the filter is the
        server's, and an empty page it answers is not an empty endpoint.
      */}
      {allRequests.length === 0 && !filterApplied ? (
        <Panel
          className="endpoint-empty"
          title="Captured Requests"
          subtitle="Nothing has arrived yet — send a request to the URL above and it will appear here."
          actions={
            <>
              {liveToggle}
              <Button variant="outline-secondary" size="sm" onClick={() => fetchEndpointData()}>
                <FontAwesomeIcon icon={faSync} aria-hidden="true" className="me-2" />
                Refresh
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                <FontAwesomeIcon icon={faTrash} aria-hidden="true" className="me-2" />
                Delete Endpoint
              </Button>
            </>
          }
        >
          <div className="endpoint-empty__body">
            <FontAwesomeIcon icon={faInbox} aria-hidden="true" className="endpoint-empty__icon" />
            <p className="mb-0">
              {autoRefresh
                ? 'Waiting for requests. This page is checking every few seconds.'
                : 'Waiting for requests. Refresh once something has been sent, or turn on Live.'}
            </p>
          </div>

          {/*
            Starter help, and only starter help: an endpoint that already holds
            a request shows the list instead. The command is built from the same
            absolute URL the panel above displays, so what is copied is exactly
            what is run.
          */}
          <div className="endpoint-starter" data-testid="starter-curl">
            <div className="endpoint-starter__head">
              <h4 className="endpoint-starter__title">
                <FontAwesomeIcon icon={faTerminal} aria-hidden="true" className="endpoint-starter__icon" />
                Send your first request
              </h4>
              <CopyButton
                field="starter-curl"
                text={starterCurl}
                label="Copy starter cURL command"
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
            </div>
            <pre className="code-block code-block--terminal" style={{ overflowX: 'auto' }}>
              <code>{starterCurl}</code>
            </pre>
            <p className="detail-hint">
              Paste this into your terminal. Running it captures a request, and this panel shows the
              row once it arrives — turn on Live above, or press Refresh.
            </p>
          </div>
        </Panel>
      ) : (
        <Panel
          className="endpoint-requests"
          title="Captured Requests"
          subtitle="Select a request to inspect its headers, body and cURL command."
          actions={
            <>
              {liveToggle}
              <Button variant="outline-secondary" size="sm" onClick={() => fetchEndpointData()}>
                <FontAwesomeIcon icon={faSync} aria-hidden="true" className="me-2" />
                Refresh
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={exportRequests}>
                <FontAwesomeIcon icon={faDownload} aria-hidden="true" className="me-2" />
                Export All
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => setShowClearConfirm(true)}>
                <FontAwesomeIcon icon={faTrash} aria-hidden="true" className="me-2" />
                Clear All
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                <FontAwesomeIcon icon={faTrash} aria-hidden="true" className="me-2" />
                Delete Endpoint
              </Button>
            </>
          }
        >
          <div className="request-filters">
            <InputGroup size="sm" className="request-filters__search">
              <InputGroup.Text>
                <FontAwesomeIcon icon={faFilter} aria-hidden="true" />
              </InputGroup.Text>
              <Form.Control
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter every request by method, path, body or IP..."
                aria-label="Filter requests"
              />
            </InputGroup>
            <Form.Select
              size="sm"
              className="request-filters__method"
              value={methodFilter}
              onChange={(event) => setMethodFilter(event.target.value)}
              aria-label="Filter by method"
            >
              <option value="all">All methods</option>
              {methods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </Form.Select>
            <span className="request-filters__count">
              {allRequests.length} of {data.requestCount} shown
            </span>
          </div>
          {/*
            Two different promises, and the hint has to keep them apart: the
            filter is a query the server runs over every row the endpoint holds,
            while Export All still writes only the rows this browser downloaded.
          */}
          <p className="request-filters__hint">
            Filtering covers every request this endpoint holds. Export All writes only the{' '}
            {allRequests.length} loaded request
            {allRequests.length === 1 ? '' : 's'}
            {hasOlderRequests
              ? `, not the ${data.requestCount} ${
                  filterApplied ? 'the filter matches' : 'this endpoint holds'
                }.`
              : '.'}
          </p>

          {allRequests.length === 0 ? (
            <div className="endpoint-empty__body">
              <FontAwesomeIcon icon={faInbox} aria-hidden="true" className="endpoint-empty__icon" />
              <p className="mb-0">No captured request matches this filter.</p>
            </div>
          ) : (
            <div className="split-layout">
              <div className="split-layout__aside">
                <ul className="request-list" data-endpoint-count={allRequests.length}>
                  {allRequests.map((request, index) => {
                    const isSelected = activePreview ? activePreview.id === request.id : false;
                    return (
                      <li key={request.id} className="request-list__item">
                        <button
                          type="button"
                          className={`request-row${isSelected ? ' request-row--selected' : ''}`}
                          ref={(element) => {
                            rowRefs.current[index] = element;
                          }}
                          onClick={() => selectRequest(index)}
                          onKeyDown={(event) => handleRowKeyDown(event, index)}
                          aria-pressed={isSelected}
                        >
                          <span className="request-row__main">
                            <Badge bg={methodVariant(request.method)} className="method-badge">
                              {request.method}
                            </Badge>
                            <span className="request-row__time">
                              <FontAwesomeIcon icon={faClock} aria-hidden="true" className="me-2" />
                              {formatDate(request.receivedAt)}
                            </span>
                          </span>
                          <span className="request-row__meta">
                            {request.path ? <code>{request.path}</code> : null}
                            {typeof request.status === 'number' ? (
                              <Badge bg={statusVariant(request.status)} className="status-badge">
                                {request.status}
                              </Badge>
                            ) : null}
                            {request.ip ? <code>{request.ip}</code> : null}
                            {request.queryParamCount > 0 ? (
                              <span>
                                {request.queryParamCount} query param
                                {request.queryParamCount === 1 ? '' : 's'}
                              </span>
                            ) : null}
                            {request.bodyBytes > 0 ? (
                              <span>body · {formatBytes(request.bodyBytes)}</span>
                            ) : null}
                            {request.bodyPreview ? (
                              <code className="request-row__snippet">{request.bodyPreview}</code>
                            ) : null}
                          </span>
                          <span className="request-row__aside">
                            <Badge bg={retentionVariant(request.daysUntilDeletion)} className="retention-badge">
                              {retentionLabel(request.daysUntilDeletion)}
                            </Badge>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              aria-hidden="true"
                              className="request-row__chevron"
                            />
                          </span>
                        </button>
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          className="request-row__delete"
                          onClick={() => deleteRequest(request)}
                          disabled={deletingId === request.id}
                          aria-label={`Delete request ${request.id}`}
                          title="Delete this request"
                        >
                          <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
                {hasOlderRequests ? (
                  <div className="request-list__more">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={loadOlderRequests}
                      disabled={loadingOlder}
                    >
                      <FontAwesomeIcon icon={faHistory} aria-hidden="true" className="me-2" />
                      {loadingOlder
                        ? 'Loading older requests...'
                        : `Load older requests (${data.requestCount - allRequests.length} more)`}
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className="split-layout__main">
                {activePreview ? (
                  activeRequest ? (
                    <RequestDetail
                      request={activeRequest}
                      slug={data.endpoint.slug}
                      apiUrl={PUBLIC_API_BASE}
                      onExport={exportSingleRequest}
                    />
                  ) : activeDetailError ? (
                    <Alert variant="danger" className="mb-0">
                      {activeDetailError}
                    </Alert>
                  ) : (
                    <div className="detail-loading" role="status">
                      <Spinner animation="border" aria-hidden="true" />
                      <p className="mb-0">Loading request...</p>
                    </div>
                  )
                ) : (
                  <div className="detail-empty">
                    <FontAwesomeIcon icon={faInbox} aria-hidden="true" className="detail-empty__icon" />
                    <p className="mb-0">Select a request to inspect it.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </Panel>
      )}

      {!isDesktop ? (
        <Modal show={Boolean(selectedRequest)} onHide={() => setSelectedRequest(null)} size="lg" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>Request Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest ? (
              details[selectedRequest.id] ? (
                <RequestDetail
                  request={details[selectedRequest.id]}
                  slug={data.endpoint.slug}
                  apiUrl={PUBLIC_API_BASE}
                  onExport={exportSingleRequest}
                />
              ) : detailError?.id === selectedRequest.id ? (
                <Alert variant="danger" className="mb-0">
                  {detailError.message}
                </Alert>
              ) : (
                <div className="detail-loading" role="status">
                  <Spinner animation="border" aria-hidden="true" />
                  <p className="mb-0">Loading request...</p>
                </div>
              )
            ) : null}
          </Modal.Body>
        </Modal>
      ) : null}

      <Modal show={showClearConfirm} onHide={() => setShowClearConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Clear captured requests?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            {/* The endpoint's own total, not the filtered one: this deletes every
                captured request, whatever the filter on the list says. */}
            This permanently deletes {endpointRequestCount} captured request
            {endpointRequestCount === 1 ? '' : 's'} for endpoint <code>{data.endpoint.slug}</code>.
          </p>
          <p className="text-muted mb-0">
            The endpoint URL keeps working and your response settings are unchanged.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearConfirm(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={clearAllRequests} disabled={clearing}>
            {clearing ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Deleting...
              </>
            ) : (
              'Yes, clear all'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete this endpoint?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This permanently deletes endpoint <code>{data.endpoint.slug}</code> and all{' '}
            {endpointRequestCount} captured request{endpointRequestCount === 1 ? '' : 's'}.
          </p>
          <p className="text-muted mb-0">
            The URL stops working: anything sent to it afterwards gets a 404. Clear All only
            removes the captured requests and leaves the URL capturing.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deletingEndpoint}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteEndpoint} disabled={deletingEndpoint}>
            {deletingEndpoint ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Deleting...
              </>
            ) : (
              'Yes, delete endpoint'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSettings} onHide={() => setShowSettings(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Response Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* A rejection the API did not tie to one of the fields below. */}
          {settingsError && settingsError.field === null ? (
            <Alert variant="danger" className="py-2" role="alert">
              {settingsError.message}
            </Alert>
          ) : null}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Response Status Code</Form.Label>
              <Form.Control
                type="number"
                min="200"
                max="999"
                value={responseStatusCode}
                onChange={(event) => setResponseStatusCode(event.target.value)}
                isInvalid={settingsError?.field === 'responseStatusCode'}
              />
              {settingsError?.field === 'responseStatusCode' ? (
                <div className="invalid-feedback d-block" role="alert">
                  {settingsError.message}
                </div>
              ) : (
                <Form.Text className="text-muted">
                  Any HTTP status from 200 to 999. A cleared field is rejected, never
                  silently replaced with 200.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content-Type</Form.Label>
              <Form.Control
                type="text"
                value={responseContentType}
                onChange={(event) => setResponseContentType(event.target.value)}
                placeholder="application/json"
                isInvalid={settingsError?.field === 'responseContentType'}
              />
              {settingsError?.field === 'responseContentType' ? (
                <div className="invalid-feedback d-block" role="alert">
                  {settingsError.message}
                </div>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Response Headers</Form.Label>
              {headerRows.length === 0 ? (
                <Form.Text className="text-muted d-block mb-2">
                  No extra headers. A header list sent to a capture does not
                  configure this: the endpoint answers with its Content-Type alone.
                </Form.Text>
              ) : null}
              {headerRows.map((row, index) => {
                const rowError = responseHeaderRowError(settingsError, row.name);

                return (
                  <div className="settings-header-row" key={index}>
                    <div className="settings-header-row__fields">
                      <Form.Control
                        type="text"
                        value={row.name}
                        placeholder="X-Trace-Id"
                        aria-label={`Header ${index + 1} name`}
                        onChange={(event) => updateHeaderRow(index, { name: event.target.value })}
                        isInvalid={rowError !== null}
                      />
                      <Form.Control
                        type="text"
                        value={row.value}
                        placeholder="value"
                        aria-label={`Header ${index + 1} value`}
                        onChange={(event) => updateHeaderRow(index, { value: event.target.value })}
                        isInvalid={rowError !== null}
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        aria-label={`Remove header ${index + 1}`}
                        onClick={() => removeHeaderRow(index)}
                      >
                        <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                      </Button>
                    </div>
                    {rowError ? (
                      <div className="invalid-feedback d-block" role="alert">
                        {rowError}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {/* A rejection of the list itself — too many headers, or a bag
                  over the size limit — belongs to no single row. */}
              {headersGroupError ? (
                <div className="invalid-feedback d-block" role="alert">
                  {headersGroupError}
                </div>
              ) : null}
              <Button variant="outline-secondary" size="sm" onClick={addHeaderRow}>
                <FontAwesomeIcon icon={faPlus} aria-hidden="true" className="me-2" />
                Add header
              </Button>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Response Body</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={responseBody ?? ''}
                onChange={(event) => setResponseBody(event.target.value)}
                placeholder='{"message": "Hello from webhook!"}'
                isInvalid={settingsError?.field === 'responseBody'}
              />
              {settingsError?.field === 'responseBody' ? (
                <div className="invalid-feedback d-block" role="alert">
                  {settingsError.message}
                </div>
              ) : (
                <>
                  <Form.Text className="text-muted">
                    Clearing the field returns no body; leaving it untouched keeps the
                    endpoint's default response. JSON is re-serialised, so it is always
                    valid JSON on the wire.
                  </Form.Text>
                  {isBodylessStatus(responseStatusCode) ? (
                    <Form.Text className="text-warning d-block" role="status">
                      A {responseStatusCode.trim()} response carries no body, so this will
                      not be sent.
                    </Form.Text>
                  ) : null}
                </>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSettings(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveSettings} disabled={settingsSaving}>
            {settingsSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
};

export default EndpointView;
